<template>
  <div class="otp-page">
    <section class="hero-panel">
      <img
        :src="require(`../brand/${brandConfig.brand}/assets/evina-img.webp`)"
        class="hero-image"
        alt="Battle League Gaming"
      />
      <div class="hero-shadow"></div>
    </section>

    <div class="content-panel">
      <img
        :src="require(`../brand/${brandConfig.brand}/assets/logo.png`)"
        class="brand-logo"
        alt="BLG"
      />

      <div class="welcome-copy">
        <div>Welcome to</div>
        <div>Battle League Gaming</div>
      </div>

      <div class="body-copy">
        Where you'll have the chance to compete, conquer,
        and rise in the ranks of tournament gaming!
      </div>

      <div v-if="brandConfig.heroTagline || brandConfig.price" class="service-price-block">
        <div v-if="brandConfig.price" class="service-price">
          {{ brandConfig.price }}
        </div>
        <div v-if="brandConfig.heroTagline" class="service-tagline">
          {{ brandConfig.heroTagline }}
        </div>
      </div>

      <div class="instruction-copy">
        Please enter the otp sent via sms to join
        Battle League Gaming
      </div>

      <div class="otp-row">
        <input
          v-for="(digit, index) in otpDigits"
          :key="index"
          :value="digit"
          class="otp-input"
          type="text"
          maxlength="1"
          inputmode="numeric"
          autocomplete="one-time-code"
          @input="onOtpInput(index, $event)"
          @keydown.backspace="onBackspace(index, $event)"
          :ref="setOtpRef"
        />
      </div>

      <div v-if="inlineMessage" :class="['inline-message', `inline-message-${inlineMessageType}`]">
        {{ inlineMessage }}
      </div>

      <button
        class="primary-button"
        :disabled="isContinueDisabled"
        @click="onContinueClicked"
      >
        Continue
      </button>

      <button class="secondary-button" @click="onCancelClicked">
        Cancel
      </button>

      <button class="link-button" @click="onResendOtpClicked">
        Resend OTP
      </button>

      <div v-if="brandConfig.subscriptionDisclosure" class="subscription-disclosure">
        {{ brandConfig.subscriptionDisclosure }}
      </div>

      <div class="terms-copy">
        By continuing you agree to the
        <br>
        <a
          :href="brandConfig.termsAndConditionsUrl"
          target="_blank"
          rel="noreferrer"
        >
          terms &amp; conditions
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import SingtelSubscriptionService from "@/services/singtelSubscriptionService";
import { trackEvent } from "@/functions/trackEvent";

const SESSION_ID_STORAGE_KEY = "singtelSessionId";
const OTP_DIGITS_COUNT = 6;
const ACCEPTED_VALIDATE_OTP_RESULT_CODES = [100, 150, 151];

export default {
  data() {
    return {
      otpDigits: Array.from({ length: OTP_DIGITS_COUNT }, () => ""),
      otpInputRefs: [],
      isSubmitting: false,
      lastSubmittedKey: "",
      inlineMessage: "",
      inlineMessageType: "error",
    };
  },
  computed: {
    ...mapState(["brandConfig", "sessionId"]),
    otpValue() {
      return this.otpDigits.join("");
    },
    currentSubmissionKey() {
      return `${this.resolveSessionId()}|${this.otpValue}`;
    },
    isDuplicateSubmission() {
      return (
        !!this.lastSubmittedKey &&
        this.lastSubmittedKey === this.currentSubmissionKey
      );
    },
    isContinueDisabled() {
      return this.isSubmitting || this.isDuplicateSubmission;
    },
  },
  mounted() {
    const sessionId = this.resolveSessionId();

    if (!sessionId) {
      this.setInlineMessage("Missing session information. Please restart the subscription flow.");
      this.$router.replace("/");
      return;
    }

    this.setSessionId(sessionId);
  },
  methods: {
    ...mapMutations(["setSessionId"]),
    setInlineMessage(message, type = "error") {
      this.inlineMessage = message;
      this.inlineMessageType = type;
    },
    setOtpRef(element) {
      if (!element || this.otpInputRefs.includes(element)) {
        return;
      }

      this.otpInputRefs.push(element);
    },
    resolveSessionId() {
      return (
        this.$route.query.sessionId ||
        this.sessionId ||
        sessionStorage.getItem(SESSION_ID_STORAGE_KEY) ||
        ""
      );
    },
    onOtpInput(index, event) {
      this.inlineMessage = "";
      const inputValue = (event.target.value || "").replace(/\D/g, "");
      const digit = inputValue.slice(-1);
      this.otpDigits.splice(index, 1, digit);
      event.target.value = digit;

      if (digit && index < this.otpInputRefs.length - 1) {
        this.otpInputRefs[index + 1].focus();
      }
    },
    onBackspace(index, event) {
      if (this.otpDigits[index]) {
        this.otpDigits.splice(index, 1, "");
        return;
      }

      if (index > 0) {
        this.otpInputRefs[index - 1].focus();
        this.otpDigits.splice(index - 1, 1, "");
        event.preventDefault();
      }
    },
    async onContinueClicked() {
      if (this.isContinueDisabled) {
        return;
      }

      trackEvent("subscribe_confirm_click");

      this.inlineMessage = "";
      const sessionId = this.resolveSessionId();

      if (!sessionId) {
        this.setInlineMessage("Missing session information. Please restart the subscription flow.");
        this.$router.replace("/");
        return;
      }

      if (this.otpValue.length !== this.otpDigits.length) {
        this.setInlineMessage(`Please enter the ${this.otpDigits.length}-digit OTP.`);
        return;
      }

      this.isSubmitting = true;
      this.lastSubmittedKey = `${sessionId}|${this.otpValue}`;

      try {
        const response = await SingtelSubscriptionService.validateOTP(sessionId, this.otpValue);

        if (ACCEPTED_VALIDATE_OTP_RESULT_CODES.includes(response?.resultCode)) {
          sessionStorage.removeItem(SESSION_ID_STORAGE_KEY);
          this.$router.push("/thank-you");
          return;
        }

        this.setInlineMessage(response?.resultText || "OTP validation failed.");
      } catch (error) {
        const message =
          error.response?.data?.resultText ||
          error.response?.data?.message ||
          error.message ||
          "OTP validation failed.";

        this.setInlineMessage(message);
      } finally {
        this.isSubmitting = false;
      }
    },
    onCancelClicked() {
      this.$router.push("/");
    },
    onResendOtpClicked() {
      // TODO: Call resend OTP endpoint when it is available.
      this.setInlineMessage("Resend OTP integration is not available yet.", "info");
    },
  },
};
</script>

<style scoped>
.otp-page {
  min-height: 100vh;
  background: #020202;
  color: #ffffff;
  display: flex;
  flex-direction: column;
}

.hero-panel {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.hero-image {
  width: 100%;
  min-height: 56vh;
  max-height: 66vh;
  object-fit: cover;
  object-position: center top;
  display: block;
}

.hero-shadow {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(0, 0, 0, 0.28) 0%, rgba(0, 0, 0, 0) 14%, rgba(0, 0, 0, 0.18) 58%, rgba(2, 2, 2, 0.96) 100%),
    linear-gradient(180deg, rgba(2, 2, 2, 0) 0%, rgba(2, 2, 2, 0.9) 92%, #020202 100%);
}

.content-panel {
  position: relative;
  z-index: 3;
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: -32rem;
  padding: 0 1.35rem 1.5rem;
  text-align: center;
}

.brand-logo {
  width: 138px;
  height: auto;
  margin-top: 0.3rem;
}

.welcome-copy {
  margin-top: 1.4rem;
  color: #ffffff;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.35;
}

.body-copy {
  max-width: 18.5rem;
  margin-top: 0.95rem;
  color: rgba(255, 255, 255, 0.9);
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.25;
}

.service-price-block {
  margin-top: 1.6rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
}

.service-price {
  color: #B3F50D;
  font-family: "Clash Display", sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.1;
}

.service-tagline {
  color: #FFFFFF;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
}

.instruction-copy {
  max-width: 16rem;
  margin-top: 3rem;
  color: #ffffff;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
}

.otp-row {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  width: 100%;
  max-width: 320px;
  justify-content: space-between;
}

.otp-input {
  flex: 1 1 0;
  min-width: 0;
  width: auto;
  aspect-ratio: 1 / 1;
  max-width: 44px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  background: rgba(50, 50, 50, 0.88);
  color: #ffffff;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 20px;
  font-weight: 700;
  outline: none;
  padding: 0;
}

.otp-input:focus {
  border-color: #c8ff3d;
  box-shadow: 0 0 0 2px rgba(200, 255, 61, 0.2);
}

.inline-message {
  margin-top: 0.8rem;
  min-height: 1.2rem;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
}

.inline-message-error {
  color: #ff9d9d;
}

.inline-message-info {
  color: #c8ff3d;
}

.primary-button,
.secondary-button {
  width: 100%;
  border-radius: 6px;
  font-family: Inter, sans-serif;
  font-size: 25px;
  font-weight: 700;
  line-height: 1;
  border: none;
}

.primary-button {
  margin-top: 1.55rem;
  min-height: 90px;
  background: #c8ff3d;
  color: #000000;
}

.primary-button:disabled {
  background: #4a4a4a;
  color: rgba(255, 255, 255, 0.45);
  opacity: 0.65;
  cursor: not-allowed;
}

.secondary-button {
  margin-top: 0.75rem;
  min-height: 90px;
  background: rgba(18, 18, 18, 0.58);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.link-button {
  margin-top: 1.35rem;
  background: transparent;
  border: none;
  color: #c8ff3d;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 800;
  text-decoration: underline;
}

.subscription-disclosure {
  max-width: 22rem;
  margin-top: auto;
  padding-top: 1.4rem;
  color: rgba(255, 255, 255, 0.7);
  font-family: Inter, sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: 1.45;
}

.terms-copy {
  margin-top: 0.9rem;
  color: rgba(255, 255, 255, 0.596);
  font-family: Inter, sans-serif;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.25;
}

.terms-copy a {
  color: rgba(255, 255, 255, 0.596);
}
</style>
